// translations/pages/kyveil/bg.js

export const kyveilPageBG = {
  "seo": {
    "title": "Kyveil – анонимизация на лични данни (PII) | pAIoneer Labs GmbH",
    "description": "Kyveil разпознава и анонимизира лични данни локално във Вашите системи. Настолно приложение или REST API за анонимизация на лични данни в съответствие с GDPR.",
    "keywords": "Kyveil, анонимизация на лични данни, PII анонимизация, анонимизиране на документи, защита на данните, GDPR, Microsoft Presidio, spaCy, flair, локална обработка на данни",
    "ogTitle": "Kyveil: професионална анонимизация на лични данни с ИИ и NLP",
    "ogDescription": "Автоматично разпознаване и анонимизиране на лични данни като настолно приложение или REST API във Вашата собствена инфраструктура.",
    "twitterTitle": "Kyveil – анонимизация на лични данни",
    "twitterDescription": "Локално разпознаване и анонимизиране на лични данни в чувствителни документи.",
    "url": "https://paioneer-labs.eu/kyveil",
    "structuredData": {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Kyveil",
      "applicationCategory": "SecurityApplication",
      "operatingSystem": "Windows, macOS, Linux, Server",
      "description": "Софтуер за локално разпознаване и анонимизиране на лични данни с ИИ и NLP.",
      "provider": {
        "@type": "Organization",
        "name": "pAIoneer Labs GmbH",
        "url": "https://paioneer-labs.eu"
      },
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "url": "https://paioneer-labs.eu/kyveil"
      }
    }
  },
  "hero": {
    "eyebrow": "Защита на данните и анонимизация",
    "title": "Kyveil",
    "subtitle": "Локална анонимизация преди ИИ, тестове и споделяне.",
    "description": "Kyveil е локален слой за анонимизация на документи и работни процеси с ИИ. Софтуерът разпознава лични данни във файлове Word, Excel, PDF и текст, заменя ги или ги маскира и показва предварителен преглед, преди съдържанието да влезе в ИИ чатове, тестове, анализи или външно споделяне. Kyveil се разработва и непрекъснато надгражда чрез [IDD](idd) (Intent-Driven Development) и автоматизирано с [Veridion](veridion) от нашите ИИ служители с участие на човек в цикъла.",
    "platforms": "Настолно приложение, REST API и AnonChat",
    "proofPoints": [
      "Документите остават локално във Вашите системи.",
      "Личните данни се заменят или маскират.",
      "Предварителен преглед показва анонимизираното съдържание преди споделяне или ИИ чат.",
      "Word, Excel, PDF и текст са основните работни процеси с документи."
    ]
  },
  "dataSovereignty": {
    "title": "Вашите данни. Вашите системи. Вашият контрол.",
    "subtitle": "Пълен суверенитет на данните чрез локална обработка.",
    "items": [
      {
        "title": "Без нужда от облак",
        "description": "Цялата обработка се извършва локално във Вашата инфраструктура. Файловете Ви никога не напускат Вашите системи. Без зависимост от облака, без предаване на данни навън."
      },
      {
        "title": "Периферни изчисления",
        "description": "Настолното приложение работи напълно самостоятелно на работните станции. API се разполага на Вашите собствени сървъри. И двата варианта обработват всичко локално в рамките на Вашия периметър на сигурност."
      },
      {
        "title": "Автономия на данните",
        "description": "Пълен контрол върху Вашите потоци от данни. Без обвързване с доставчик. Без текущи разходи за облак. Чувствителната Ви информация остава единствено под Ваш контрол."
      },
      {
        "title": "Стандартен хардуер",
        "description": "Не е необходим GPU. Работи на обикновени офис компютри и стандартни сървъри. Не са нужни инвестиции в специализиран хардуер."
      }
    ]
  },
  "features": {
    "title": "Основни функции",
    "subtitle": "Всичко необходимо за контролирано разпознаване и анонимизиране на лични данни в бизнес процесите.",
    "items": [
      {
        "title": "Разпознаване на лични данни с ИИ",
        "description": "Автоматично разпознаване с Microsoft Presidio SDK, spaCy на Explosion AI и flair на Humboldt-Universität zu Berlin.",
        "tone": "primary"
      },
      {
        "title": "Режим на заместване (Replace)",
        "description": "Заменя разпознатите лични данни с общи заместители като [NAME], [EMAIL] и [ADDRESS] за сценарии, свързани с нормативното съответствие.",
        "tone": "info"
      },
      {
        "title": "Режим с фиктивни данни (Fake)",
        "description": "Заменя разпознатите лични данни с правдоподобни заместващи стойности за тестови среди и разработка.",
        "tone": "warning"
      },
      {
        "title": "Множество формати на документи",
        "description": "Обработва файлове на Microsoft Word, Excel, OpenDocument, PDF и обикновен текст.",
        "tone": "success"
      },
      {
        "title": "Многоезична поддръжка",
        "description": "Вградена поддръжка за немски, английски, френски, италиански и испански с NLP модели за съответния език.",
        "tone": "secondary"
      },
      {
        "title": "Конфигурируемо разпознаване",
        "description": "Определете кои категории лични данни да се разпознават: имена, адреси, имейли, телефонни номера, банкови сметки, данъчни номера и други.",
        "tone": "primary"
      },
      {
        "title": "Разширяемост",
        "description": "Интерфейси за собствена логика за предварителна и последваща обработка с цел интеграция във Вашите конкретни работни процеси.",
        "tone": "info"
      },
      {
        "title": "Оптимизирано за CPU",
        "description": "Ефективната архитектура на моделите работи на стандартен офис хардуер, без да изисква GPU.",
        "tone": "warning"
      }
    ]
  },
  "technology": {
    "title": "Технологичен стек",
    "subtitle": "Фреймуърци от корпоративен клас и доказани изследвания в областта на NLP.",
    "items": [
      {
        "title": "Microsoft Presidio SDK",
        "description": "Microsoft Data Protection and De-identification SDK. Утвърден в индустрията фреймуърк за разпознаване и анонимизиране на лични данни с вградени разпознаватели на същности.",
        "tone": "primary"
      },
      {
        "title": "spaCy",
        "subtitle": "Разработено от Explosion AI",
        "description": "Обработка на естествен език от промишлен клас. Готова за продукционна употреба NLP библиотека с предварително обучени статистически модели за разпознаване на именувани същности.",
        "tone": "info"
      },
      {
        "title": "flair",
        "subtitle": "Разработено от Humboldt-Universität zu Berlin",
        "description": "Съвременен NLP фреймуърк с контекстуални вграждания на низове (contextual string embeddings). Усъвършенствани невронни модели за по-висока точност при разпознаването на същности.",
        "tone": "secondary"
      }
    ],
    "integration": "Интегриран ИИ пайплайн",
    "integrationDesc": "Комбинирано разпознаване с Presidio като слой за оркестрация и с модели на spaCy и flair за оптимално разпознаване при множество езици и типове същности."
  },
  "technicalDetails": {
    "title": "Технически възможности",
    "subtitle": "Конфигурируемо разпознаване за европейски бизнес документи.",
    "items": [
      {
        "title": "Езикова поддръжка",
        "description": "Предварително обучени NLP модели за немски, английски, френски, италиански и испански. Разпознаване на същности според езика за точно откриване на лични данни в европейски бизнес контекст.",
        "tone": "primary"
      },
      {
        "title": "Конфигурируеми категории лични данни",
        "description": "Изберете кои видове лични данни да се разпознават и анонимизират: имена на лица, имейл адреси, телефонни номера, пощенски адреси, банкови сметки по IBAN, номера на кредитни карти, социалноосигурителни номера, данъчни номера, номера на паспорти и други.",
        "tone": "info"
      },
      {
        "title": "Режими на анонимизация",
        "description": "Режимът на заместване (Replace) заменя личните данни с общи заместители за документация по нормативното съответствие. Режимът с фиктивни данни (Fake) използва правдоподобни заместващи стойности за тестови среди, като запазва структурата на документа.",
        "tone": "warning"
      },
      {
        "title": "Предварителна и последваща обработка",
        "description": "Реализирайте собствена логика преди и след анонимизацията. Чрез предоставените интерфейси добавете валидиране на документи, преобразуване на формати, проверки на качеството, одитни записи или интеграция с последващи системи.",
        "tone": "secondary"
      }
    ]
  },
  "anonChat": {
    "title": "AnonChat разширява Kyveil с уеб чат с ИИ",
    "subtitle": "Новото приложение използва същия подход за анонимизация преди същинския чат с външни или вътрешни ИИ системи.",
    "eyebrow": "Ново приложение",
    "name": "AnonChat",
    "description": "AnonChat е уеб чат с ИИ, в който документи на Word и Excel могат да бъдат анонимизирани преди използване с ChatGPT, Claude, Gemini или вътрешни LLM. Предварителен преглед показва анонимизирания текст, преди съдържанието да бъде прехвърлено в чата.",
    "statement": "Така решението остава у потребителя: проверка, одобрение и едва след това чат.",
    "steps": [
      {
        "title": "Зареждане на документ",
        "description": "Файловете на Word или Excel се подготвят за анонимизация, преди да влязат в работния процес с ИИ.",
        "tone": "primary"
      },
      {
        "title": "Анонимизиране на личните данни",
        "description": "Kyveil разпознава личните данни и ги заменя или маскира според избраните правила.",
        "tone": "info"
      },
      {
        "title": "Проверка на прегледа",
        "description": "Анонимизираният текст е видим, преди съдържанието да бъде предадено на чата.",
        "tone": "warning"
      },
      {
        "title": "Използване на ИИ чата",
        "description": "Едва след проверката анонимизираното съдържание се използва за чат с външни или вътрешни LLM.",
        "tone": "success"
      }
    ]
  },
  "deployment": {
    "title": "Варианти за внедряване",
    "subtitle": "Изберете подходящото решение за Вашия случай на употреба.",
    "options": [
      {
        "title": "Настолно приложение",
        "subtitle": "Самостоятелно приложение за отделни работни места",
        "description": "Настолната версия работи напълно самостоятелно под Windows, macOS или Linux. Идеална е за потребители, които трябва да анонимизират документи директно на работното си място, без зависимост от мрежа.",
        "features": [
          "100% работа офлайн – не е необходима интернет връзка",
          "Файловете никога не напускат локалната работна станция",
          "Пакетна обработка на множество файлове",
          "Графичен потребителски интерфейс за лесна работа",
          "Работи на стандартни офис компютри без GPU",
          "Еднократен лиценз – без абонаментен модел"
        ]
      },
      {
        "title": "REST API",
        "subtitle": "Внедряване на сървър за интеграция и мащабиране",
        "description": "API версията се разполага на Вашите собствени сървъри или в частна облачна инфраструктура. Идеална е за интегриране на анонимизацията в съществуващи системи и работни процеси с голям обем на обработка.",
        "features": [
          "Внедряване във Вашата собствена инфраструктура – локално или в частен облак",
          "Хоризонтално мащабиране при високи изисквания за пропускателна способност",
          "RESTful API с изчерпателна документация",
          "Същият механизъм за анонимизация като в настолната версия",
          "Примери за интеграция с разпространени платформи",
          "Поддръжка на персонализирана предварителна и последваща обработка"
        ]
      },
      {
        "title": "AnonChat",
        "subtitle": "Уеб чат с ИИ с анонимизация преди чата",
        "description": "AnonChat поставя Kyveil пред същинския ИИ чат. Документите на Word и Excel се анонимизират, предварителният преглед се проверява и едва след това съдържанието се използва с ChatGPT, Claude, Gemini или вътрешни LLM.",
        "features": [
          "Анонимизация преди чата вместо последващ контрол",
          "Предварителен преглед на анонимизирания текст преди предаването",
          "Предназначен за въпроси към ИИ системи въз основа на документи",
          "Намалява риска лични данни да бъдат изпратени към външни модели",
          "Може да се свърже с вътрешни LLM или корпоративни чат решения",
          "Използва същия подход за анонимизация като настолното приложение и REST API"
        ]
      }
    ],
    "bothNote": "Настолното приложение, REST API и AnonChat са предназначени да анонимизират чувствителното съдържание контролирано преди по-нататъшната му обработка."
  },
  "useCases": {
    "title": "Случаи на употреба",
    "subtitle": "От съответствие с GDPR до безопасни данни за разработка.",
    "items": [
      {
        "title": "Съответствие с GDPR",
        "description": "Анонимизирайте личните данни, преди да споделите документи с външни лица или с вътрешни отдели. Изпълнете изискванията за защита на данните при тестови данни, набори от данни за анализ и документация.",
        "tone": "primary"
      },
      {
        "title": "Подготовка на тестови данни",
        "description": "Подгответе реалистични набори от тестови данни, така че разработчиците да работят с данни, близки до продукционните, без да се разкрива истинска лична информация.",
        "tone": "info"
      },
      {
        "title": "Обработка на документи за човешки ресурси",
        "description": "Анонимизирайте кандидатури, служебни досиета и документация за човешки ресурси за безпристрастни оценки, одити за съответствие или анализи на данни при запазване на неприкосновеността на личния живот.",
        "tone": "warning"
      },
      {
        "title": "Анализ на данни",
        "description": "Направете възможен анализа на документи с лични данни, като първо анонимизирате чувствителната информация. Споделяйте набори от данни между екипите без опасения за поверителността.",
        "tone": "success"
      },
      {
        "title": "Архивиране на документи",
        "description": "Подгответе документите за дългосрочно архивиране, като премахнете или анонимизирате личните данни съгласно правилата за съхранение и разпоредбите за защита на данните.",
        "tone": "secondary"
      },
      {
        "title": "Сигурно споделяне",
        "description": "Споделяйте документи с външни партньори, изпълнители или други отдели, след като чувствителната лична информация е била автоматично премахната.",
        "tone": "error"
      }
    ]
  },
  "releases": {
    "title": "Варианти и поддръжка",
    "subtitle": "Актуализации, разширения и поддръжка за продукционна употреба.",
    "items": [
      {
        "title": "Настолно приложение, API и AnonChat",
        "description": "Kyveil се предлага като настолно приложение и като REST API за интеграции. AnonChat е уеб чатът с анонимизация на документите преди чата.",
        "tone": "info"
      },
      {
        "title": "Поддръжка",
        "description": "За използване във фирми предлагаме техническа поддръжка, помощ при внедряването и адаптации.",
        "tone": "success"
      }
    ]
  },
  "cta": {
    "title": "Започнете с Kyveil",
    "subtitle": "Защитете чувствителните документи, преди да попаднат в ИИ, тестове, анализи или да бъдат споделени.",
    "desktop": "Заявете настолното приложение",
    "desktopSubject": "Kyveil Desktop",
    "apiContact": "Контакт за внедряване на REST API",
    "contact": "За демонстрация или оферта пишете ни.",
    "email": "info@paioneer.eu",
    "apiSubject": "Внедряване на Kyveil REST API",
    "contactSubject": "Kyveil – демонстрация или оферта"
  }
};
